import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * useExperiment — iter-212. Frontend hook for the A/B experiment
 * framework backed by `/api/experiments/*`.
 *
 * Behaviour
 * ---------
 * 1. On mount, ensures a stable ``visitor_id`` lives in localStorage
 *    (UUID-shaped, generated client-side).
 * 2. Fires `POST /experiments/{name}/exposure` once per page-load —
 *    backend assigns/returns the sticky variant. Subsequent renders
 *    reuse the cached variant without a network call.
 * 3. Returns the variant id, a loading flag, and a
 *    ``recordConversion(metadata?)`` helper.
 *
 * Stickiness: the backend keeps a `(name, visitor_id) → variant_id`
 * mapping, so even if the in-memory React cache is lost (browser
 * refresh, SPA reroute), the same visitor will see the same variant
 * across visits.
 *
 * Soft fail: if the backend doesn't recognise the experiment (paused
 * or absent), ``variant`` is null and ``isActive`` is false. Consumers
 * should fall back to the default variant in that case.
 */
const VISITOR_KEY = "lumfi_visitor_id";
// In-memory cache keyed by experiment name → variant_id so multiple
// mounted hooks on the same page hit the backend ONCE.
const variantCache = new Map();
// Track in-flight requests per experiment so concurrent mounts share
// a single network call (avoids dog-piling on `/exposure`).
const inflight = new Map();

function ensureVisitorId() {
  try {
    let v = window.localStorage.getItem(VISITOR_KEY);
    if (!v || v.length < 8) {
      v = (window.crypto?.randomUUID?.() || `v-${Math.random().toString(36).slice(2)}${Date.now()}`).slice(0, 64);
      window.localStorage.setItem(VISITOR_KEY, v);
    }
    return v;
  } catch {
    return `v-anon-${Math.random().toString(36).slice(2, 16)}`;
  }
}

export function useExperiment(name, { defaultVariant = null } = {}) {
  const [state, setState] = useState(() => {
    const cached = variantCache.get(name);
    return cached
      ? { variant: cached.variant, isActive: cached.isActive, isLoading: false }
      : { variant: defaultVariant, isActive: false, isLoading: true };
  });
  const visitorIdRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const visitorId = ensureVisitorId();
    visitorIdRef.current = visitorId;

    const cached = variantCache.get(name);
    if (cached) {
      setState({ variant: cached.variant, isActive: cached.isActive, isLoading: false });
      return () => { alive = false; };
    }

    let promise = inflight.get(name);
    if (!promise) {
      promise = api.post(`/experiments/${name}/exposure`, { visitor_id: visitorId })
        .then((r) => r.data)
        .catch(() => ({ variant_id: defaultVariant, label: null, experiment_active: false }))
        .finally(() => { inflight.delete(name); });
      inflight.set(name, promise);
    }

    promise.then((data) => {
      const entry = {
        variant: data?.variant_id ?? defaultVariant,
        isActive: !!data?.experiment_active,
      };
      variantCache.set(name, entry);
      if (alive) setState({ ...entry, isLoading: false });
    });

    return () => { alive = false; };
  }, [name, defaultVariant]);

  const recordConversion = (metadata) => {
    const visitorId = visitorIdRef.current || ensureVisitorId();
    // Fire-and-forget; the backend silently drops events without
    // an assignment so a missed exposure doesn't crash anything.
    api.post(`/experiments/${name}/conversion`, { visitor_id: visitorId, metadata: metadata || undefined })
      .catch(() => {});
  };

  return { variant: state.variant, isLoading: state.isLoading, isActive: state.isActive, recordConversion };
}
